import { FormControl, FormLabel, Stack } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import { COUNTRIES_WITH_STATES, DEFAULT_REQUIRED_FIELDS } from 'addresses/constants';
import { AddressFields } from 'addresses/enums';
import { addressService } from 'addresses/services';
import { ICountryAutocomplete, IStateAutocomplete } from 'addresses/types';
import { FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { Direction } from 'theme/enums';

interface IProps {
  fieldsPrefix?: string;
  requiredFields?: AddressFields[];
  hiddenFields?: AddressFields[];
}

export const UpsertAddressFormFields = ({
  fieldsPrefix = undefined,
  requiredFields = DEFAULT_REQUIRED_FIELDS,
  hiddenFields = [],
}: IProps) => {
  const { t } = useTranslation();

  const { control, errors, watch } = useFormContext();

  const getFieldName = (name: AddressFields) => {
    if (!fieldsPrefix) {
      return name;
    }

    return `${fieldsPrefix}.${name}`;
  };

  const getFieldError = (name: AddressFields) => {
    if (!fieldsPrefix) {
      return errors?.[name];
    }

    return errors?.[fieldsPrefix]?.[name];
  };

  const countryValue = watch(getFieldName(AddressFields.COUNTRY));

  const isStateDropdown = COUNTRIES_WITH_STATES.includes(countryValue?.abbreviation);

  const StateField = isStateDropdown ? (
    <FormField
      key={countryValue?.abbreviation}
      name={getFieldName(AddressFields.STATE)}
      type={InputType.AUTOCOMPLETE}
      inputProps={{
        control,
        error: getFieldError(AddressFields.STATE),
        placeholder: t('labelState'),
        label: t('labelState'),
        getOptions: addressService.getStatesAutocomplete,
        requestPayload: {
          filters: {
            countryAbbreviation: countryValue?.abbreviation,
          },
        },
        getLabel: (opt: IStateAutocomplete) => opt?.name,
        isClearable: false,
        required: requiredFields.includes(AddressFields.STATE),
      }}
      hidden={hiddenFields.includes(AddressFields.STATE)}
      wrapperStyle={{ width: '48%' }}
    />
  ) : (
    <FormField
      name={getFieldName(AddressFields.STATE)}
      type={InputType.TEXT_INPUT}
      inputProps={{
        control,
        error: getFieldError(AddressFields.STATE),
        placeholder: t('labelState'),
        label: t('labelState'),
        required: requiredFields.includes(AddressFields.STATE),
      }}
      hidden={hiddenFields.includes(AddressFields.STATE)}
      wrapperStyle={{ width: '48%' }}
    />
  );

  return (
    <>
      <FormField
        name={getFieldName(AddressFields.NAME)}
        type={InputType.TEXT_INPUT}
        inputProps={{
          control,
          error: getFieldError(AddressFields.NAME),
          placeholder: t('labelLocationName'),
          label: t('labelLocationName'),
          required: requiredFields.includes(AddressFields.NAME),
        }}
        hidden={hiddenFields.includes(AddressFields.NAME)}
      />

      <Stack spacing={0.5}>
        <FormControl>
          <FormLabel
            sx={{ marginBottom: '8px !important' }}
            required={
              requiredFields.includes(AddressFields.LINE1) ||
              requiredFields.includes(AddressFields.LINE2)
            }
          >
            {t('labelStreetAddress')}
          </FormLabel>
        </FormControl>

        <FormField
          name={getFieldName(AddressFields.LINE1)}
          type={InputType.TEXT_INPUT}
          inputProps={{
            control,
            error: getFieldError(AddressFields.LINE1),
            placeholder: t('labelAddressLine1'),
            required: requiredFields.includes(AddressFields.LINE1),
          }}
          hidden={hiddenFields.includes(AddressFields.LINE1)}
        />
        <FormField
          name={getFieldName(AddressFields.LINE2)}
          type={InputType.TEXT_INPUT}
          inputProps={{
            control,
            error: getFieldError(AddressFields.LINE2),
            placeholder: t('labelAddressLine2'),
            required: requiredFields.includes(AddressFields.LINE2),
          }}
          hidden={hiddenFields.includes(AddressFields.LINE2)}
        />
      </Stack>

      <Stack direction={Direction.ROW} gap={0.5}>
        <FormField
          name={getFieldName(AddressFields.COUNTRY)}
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            control,
            error: getFieldError(AddressFields.COUNTRY),
            placeholder: t('labelCountry'),
            label: t('labelCountry'),
            optionKey: 'abbreviation',
            getOptions: addressService.getCountriesAutocomplete,
            getLabel: (option: ICountryAutocomplete) => option?.name,
            disableClearable: true,
            required: requiredFields.includes(AddressFields.COUNTRY),
          }}
          wrapperStyle={{ width: '48%' }}
          hidden={hiddenFields.includes(AddressFields.COUNTRY)}
        />

        {StateField}
      </Stack>

      <Stack direction={Direction.ROW} gap={0.5}>
        <FormField
          name={getFieldName(AddressFields.CITY)}
          type={InputType.TEXT_INPUT}
          inputProps={{
            control,
            error: getFieldError(AddressFields.CITY),
            placeholder: t('labelCity'),
            label: t('labelCity'),
            required: requiredFields.includes(AddressFields.CITY),
          }}
          wrapperStyle={{ width: '48%' }}
          hidden={hiddenFields.includes(AddressFields.CITY)}
        />

        <FormField
          name={getFieldName(AddressFields.ZIP)}
          type={InputType.TEXT_INPUT}
          inputProps={{
            control,
            error: getFieldError(AddressFields.ZIP),
            placeholder: t('labelPostalCode'),
            label: t('labelPostalCode'),
            required: requiredFields.includes(AddressFields.ZIP),
          }}
          wrapperStyle={{ width: '48%' }}
          hidden={hiddenFields.includes(AddressFields.ZIP)}
        />
      </Stack>
    </>
  );
};
